"use client";
//import react
import React, { useState, useRef } from "react";
// External Lib
import { Box } from "@mui/material";
// Internal Lib
import { Input, Label, showToast, AutoComplete, DropdownAutoSuggest } from "@int-traderjoes/ui-components-library";
// Utils
import FormHeader from "@/utils/FormHeader";
import { UpArrow, DownArrow } from "@/assets/images/CommonSvgUtils";
import { isValidEmail, validatePhoneNumber, toNumberIfPossible, formatRawDropdownOptions, sanitizeVendorCorporateName } from "@/utils/helper";
import { VA_INPUT_MAX_LENGTH } from "@/utils/constants";
import {
  setConfirmationMessage,
  setConfirmationTittle,
  setButtonActionClicked,
} from "@/components/DataTable/Datatable.variables";

// constants
import Constants from "@/static/constants/constants.json";
import { isVendorFieldsLockedForExistingVendor } from "@/utils/vendor/existingVendorFieldsLock";
// Static
// Services
import AuthService from "@/services/authService";
// context
import { useTheme } from "@/context";
import { useFormContext } from "@/context/FormContext";
import { IVendorContactInformationFormData, MelissaAddressSuggestion } from "@/context/data.types";
import { useLanguageContext } from "@/context/LanguageContext";

// Styles
import "../VendorAssessment/VendorAssessment.scss";
import ConfirmationModal from "../ConfirmationModal";
import { useGlobalStateContext } from "@/context/GlobalContext";
import { handleSuggestionsFetchRequested, setAddressConfirmationMessage } from "@/services/external/mellisa";
import { set } from "lodash";
import { DropdownData } from "@/types/vendor.types";
import { getDropdownCountryName, getMelissaCountryName } from "@/utils/countryMapping";

const ADDR_RECORDS_COUNT = process.env.NEXT_PUBLIC_MELISSA_ADDRESS_SUGGESTIONS_DEFAULT_COUNT ?? '10';
interface VendorContactInformationFormProps {
  vendorKey?: number;
  isVendor?: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  formUpdated: () => void;
  isFormDisabled?: boolean;
  dropdownData: DropdownData;
  hasExistingVendor?: boolean | null;
}

const VendorContactInformationForm: React.FC<VendorContactInformationFormProps> = ({
  vendorKey,
  isVendor,
  isExpanded,
  onToggle,
  formUpdated,
  isFormDisabled,
  dropdownData,
  hasExistingVendor,
}) => {
  const { darkMode } = useTheme();
  const { setIsLoading } = useGlobalStateContext();
  const displayMode = () => {
    if (darkMode) {
      return "dark";
    } else {
      return "light";
    }
  };
  const {
    vendorContactInformationForm,
    setVendorContactInformationForm,
    vendorContactInformationFormErrors,
    setVendorContactInformationFormErrors,
    isVendorAssessmentSubmitClicked,
    vendorInformationForms,
    setVendorInformationForms,
    setVendorAssessmentForm,
    setIngredientDetailsForm,
  } = useFormContext();
  const { languageText } = useLanguageContext();
  const messageTexts = languageText?.MESSAGES;
  const toastMessageTexts = languageText?.MESSAGES?.TOAST_MESSAGES;
  const vendorContactInformationLabels = languageText?.LABELS?.VENDOR_INFORMATION?.VENDOR_CONTACT_INFO_FORM;
  const formHeader = languageText?.TABLE_AND_FORM_HEADER?.FORM_HEADER?.VENDOR_CONTACT_INFO;
  const validationMessages = languageText?.MESSAGES?.VALIDATION_MESSAGES;
  const errorTexts = languageText?.MESSAGES?.VALIDATION_MESSAGES;

  let vendorContactInformationFormPermissions: any = {};
  const payloadRef = useRef<Record<string, string>>({ maxrecords: ADDR_RECORDS_COUNT }); // Persistent object
  const [addressSuggestions, setAddressSuggestions] = useState<MelissaAddressSuggestion[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const darkModeClass = darkMode ? "dark" : "light";

  // Fetching field permissions
  vendorContactInformationFormPermissions = AuthService.fetchPermissions("vendorContactInformation");
  // Ref to store the previous address object
    const previousAddressRef = useRef({
      street: "",
      city: "",
      state: "",
      // country: "",
      postalCode: "",
    });
  // Handler function for AutoComplete input change
  const updatePayload = (field: keyof typeof vendorContactInformationForm, value: string) => {

    // Map fields to corresponding payload keys
    const fieldMap: { [key in keyof typeof vendorContactInformationForm]: string } = {
      corporateAddress: "address1",
      corporateCity: "locality",
      corporateState: "administrativearea",
      corporateCountry: "country",
      corporateZip: "postalcode",
      vendorName: "",
      contactFor: "",
      contactPersonEmailId: "",
      contactPersonPhone: ""
    };

    const payloadKey = fieldMap[field];

    if (payloadKey) {
      payloadRef.current = { ...payloadRef.current, [payloadKey]: value }; // Merge old and new values
    }
  };

  // const handleOpenModal = () =>{
  //   setIsModalOpen(true)
  // };
  // const handleCloseModal = () => {
  //   setIsModalOpen(false);
  // };
  // const handleConfirm =  () => {
  //   setIsModalOpen(false)
  // }
  const handleSelectAlternative = () => {
    // handleSuggestionsFetchRequested()
    setTimeout(() => {
      const inputElement = document.getElementById("vendor-contact-address-input");
      inputElement?.focus();
    }, 300); // Delay to allow modal to close and input to mount
    setIsModalOpen(false)

  }


  const handleAutoCompleteInputChange =
    (field: keyof typeof vendorContactInformationForm) =>
      (newValue: string) => {
        updatePayload(field, newValue);
        // Also update the payload with the current country value for better suggestions
        if (vendorContactInformationForm?.corporateCountry && dropdownData.countries.length > 0) {
        const countryId = vendorContactInformationForm.corporateCountry;
        const countryName = dropdownData.countries.find((c: any) => 
          (isNaN(Number(c.id)) ? c.id : Number(c.id)) === (isNaN(Number(countryId)) ? countryId : Number(countryId))
        )?.name;
        
        if (countryName) {
          // Map country name for Melissa API compatibility
          const melissaCountryName = getMelissaCountryName(countryName);
          updatePayload("corporateCountry", melissaCountryName);
        }
      }
        setVendorContactInformationForm((prevData) => ({
          ...prevData,
          [field]: newValue,
        }));
      };

  // Handler function for when a suggestion is selected
  const onSuggestionSelected = (
    event: React.FormEvent,
    { suggestion }: { suggestion: MelissaAddressSuggestion }
  ) => {
    const selectedAddress = suggestion.Address;

     // Find the country ID that matches the country from Melissa API
    // Try to match by ISO3166_N (numeric country code) first, then by name
    let countryMatch = dropdownData?.countries.find(
      country => country.id === selectedAddress?.ISO3166_N
    );
    
    // If no match by ISO code, try matching by country name
    if (!countryMatch) {
      countryMatch = dropdownData?.countries.find(
        country => country.name === selectedAddress?.CountryName
      );
    }
    
    // If still no match, try reverse mapping (Melissa "United States" -> dropdown "United States Of America")
    if (!countryMatch) {
      const mappedCountryName = getDropdownCountryName(selectedAddress?.CountryName);
      countryMatch = dropdownData?.countries.find(
        country => country.name === mappedCountryName
      );
    }

    setVendorContactInformationForm((prevData) => ({
      ...prevData,
      corporateZip: selectedAddress.PostalCode,
      corporateAddress: selectedAddress.Address1,
      // corporateCountry: selectedAddress.CountryName,
      corporateCity: selectedAddress.Locality,
      corporateState: selectedAddress.AdministrativeArea,
    }));

    setVendorContactInformationFormErrors((prevErrors: any) => {
      let errorsForCurrentForm = [ ...(prevErrors || {})];
      if (!selectedAddress.Address1 || selectedAddress.Address1.trim().length === 0) {
        errorsForCurrentForm=[...errorsForCurrentForm, "corporateAddress"];
      } else {
        errorsForCurrentForm=errorsForCurrentForm.filter(error => error !== "corporateAddress");
      }
      if (!selectedAddress.Locality || selectedAddress.Locality.trim().length === 0) {
        errorsForCurrentForm=[...errorsForCurrentForm, "corporateCity"];
      } else {
        errorsForCurrentForm=errorsForCurrentForm.filter(error => error !== "corporateCity");
      }
    if (!selectedAddress.Locality || selectedAddress.Locality.trim().length === 0) {
      errorsForCurrentForm=[...errorsForCurrentForm, "corporateState"];
    } else {
      errorsForCurrentForm=errorsForCurrentForm.filter(error => error !== "corporateState");
    }
    if (!selectedAddress.PostalCode || selectedAddress.PostalCode.trim().length < Constants.ZIP_CODE_MIN_LENGTH) {
      errorsForCurrentForm=[...errorsForCurrentForm, "corporateZip"];
    } else {
      errorsForCurrentForm=errorsForCurrentForm.filter(error => error !== "corporateZip");
    }
    return errorsForCurrentForm;
  });}

  const onSuggestionsClearRequested = (): void => {
    setAddressSuggestions([]);
  };

  // Input Change Handler
  const handleInputChange = (field: keyof typeof vendorContactInformationForm) => 
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let newValue = e.target.value;

      // Sanitize vendorName to prevent characters that could break base64 encoding/decoding
      if (field === "vendorName") {
        newValue = sanitizeVendorCorporateName(newValue);
      }
  
      // Return early if phone number is invalid
      if (field === "contactPersonPhone" && !validatePhoneNumber(newValue)) {
        return;
      }
  
      updateContactForm(field, newValue);
  
      if (field === "vendorName") {
        updateVendorRelatedForms(newValue);
      }
  
      formUpdated();
    };

  // Handler for browser autofill (onInput event fires for autofill)
  const handleInput = (field: keyof typeof vendorContactInformationForm) => 
    (e: React.FormEvent<HTMLInputElement>) => {
      const target = e.currentTarget;
      const newValue = target.value;
      
      // Only process if value actually changed (autofill detection)
      if (newValue !== vendorContactInformationForm[field]) {
        // Sanitize vendorName to prevent characters that could break base64 encoding/decoding
        let processedValue = newValue;
        if (field === "vendorName") {
          processedValue = sanitizeVendorCorporateName(newValue);
        }
  
        // Return early if phone number is invalid
        if (field === "contactPersonPhone" && !validatePhoneNumber(processedValue)) {
          return;
        }
  
        updateContactForm(field, processedValue);
  
        if (field === "vendorName") {
          updateVendorRelatedForms(processedValue);
        }

        // Clear error if field now has a value
        if (processedValue) {
          setVendorContactInformationFormErrors((prev) =>
            prev.filter((errorField) => errorField !== field)
          );
        }
  
        formUpdated();
      }
    };

    // Dropdown Change Handler
  const handleDropdownChange =
  (field: keyof typeof vendorContactInformationForm) =>
    (_: React.ChangeEvent<HTMLInputElement>, option: any) => {
      const isCleared =
        option == null || option.value === "" || option.value == null;

      if (isCleared) {
        setVendorContactInformationForm((prevData) => ({
          ...prevData,
          [field]: "" as any,
          ...(field === "corporateCountry"
            ? {
                corporateAddress: "",
                corporateCity: "",
                corporateState: "",
                corporateZip: "",
              }
            : {}),
        }));
        if (field === "corporateCountry") {
          setAddressSuggestions([]);
        }
        formUpdated();
        return;
      }

      setVendorContactInformationForm((prevData) => {
        const updatedForm = {
          ...prevData,
          [field]: option.value,
        };

        // Ensure corporateCountry stores the ID (not the name)
        if (field === "corporateCountry") {
          const idValue = option?.id ?? option?.value;
          updatedForm["corporateCountry"] = isNaN(Number(idValue))
            ? idValue
            : Number(idValue);
        }
        return updatedForm;
      });
        // Clear address suggestions when country changes
        if (field === 'corporateCountry') {
          setAddressSuggestions([]);
          // Clear address fields when country changes
          setVendorContactInformationForm(prevData => ({
            ...prevData,
              corporateAddress: '',
              corporateCity: '',
              corporateState: '',
              corporateZip: '',
            })
          )
        }
        
      
      formUpdated()
    };
  
  // --- Helper Functions ---
  
  const updateContactForm = (
    field: keyof typeof vendorContactInformationForm,
    value: string
  ) => {
    setVendorContactInformationForm((prevData) => ({
      ...prevData,
      [field]: value,
    }));
  };
  
  const updateVendorRelatedForms = (value: string) => {
    const safeVendorKey = vendorKey ?? 0;
  
    setVendorInformationForms((prevData) =>
      prevData.map((vendorForm, index) =>
        index === safeVendorKey
          ? { ...vendorForm, vendorCorporateName: value }
          : vendorForm
      )
    );
  
    setVendorAssessmentForm((prevData) => ({
      ...prevData,
      vendorName: value,
    }));
  
    setIngredientDetailsForm((prevData) => ({
      ...prevData,
      vendorName: value,
    }));
  };
  

  const handleFieldFocus = (
    field: keyof IVendorContactInformationFormData
  ): void => {
    setVendorContactInformationFormErrors((prevErrors) =>
      prevErrors.filter((errorField) => errorField !== field)
    );
  };


  const handleBlur = async (field: keyof IVendorContactInformationFormData) => {
    // Remove error if field has a value (handles browser autofill)
    if (vendorContactInformationForm[field]) {
      setVendorContactInformationFormErrors((prev) =>
        prev.filter((errorField) => errorField !== field)
      );
    }
    
    // Add to vendorAssessmentFormErrors if the field is empty on blur
    if (
      isVendorAssessmentSubmitClicked &&
      !vendorContactInformationForm[field] &&
      vendorContactInformationFormPermissions[field].isMandatory
    ) {
      setVendorContactInformationFormErrors((prev) => [...prev, field]);
    }
    if (
      [
        "corporateCity",
        "corporateState",
        // "corporateCountry",
        "corporateZip",
      ].includes(field)
    ) {
      const address = {
        street: vendorContactInformationForm.corporateAddress ?? "",
        city: vendorContactInformationForm.corporateCity ?? "",
        state: vendorContactInformationForm.corporateState ?? "",
        // country: vendorContactInformationForm.corporateCountry ?? "",
        postalCode: vendorContactInformationForm.corporateZip ?? "",
      };
      // Compare current address with the previous address
      const hasAddressChanged = Object.keys(address).some(
        (key) =>
          address[key as keyof typeof address] !==
          previousAddressRef.current[key as keyof typeof address]
      );
      if (hasAddressChanged) {
        //setIsLoading(true);
        // try {
        //   const isValid = await validateAddress(
        //     address,
        //     messageTexts,
        //     setIsLoading
        //   );
        //   if (isValid) {
        //     setIsModalOpen(true);
        //   } else {
        //     showToast({
        //       type: "error",
        //       message:
        //         toastMessageTexts?.MELISSA_ADDRESS_VALIDATION
        //           ?.GLOBAL_ADDRESS_VALIDATION_API_ERROR,
        //     });
        //     setIsModalOpen(true);
        //   }
        // } catch (error) {
        //   console.error("Error validating address:", error);
        // } finally {
        //   setIsLoading(false);
        // }
        setAddressConfirmationMessage(messageTexts);
        setIsModalOpen(true);
        // Update the previous address ref
        previousAddressRef.current = address;
      }
      if (field === "corporateZip") {
        const corporateZipValue = (vendorContactInformationForm[field] ?? "") as string;
        if (corporateZipValue && corporateZipValue.trim().length < Constants.ZIP_CODE_MIN_LENGTH) {
          setVendorContactInformationFormErrors((prev) => [...prev, field]);
          showToast({
            type: "error",
            message: errorTexts?.ZIP_CODE_MIN_LENGTH_ERROR
          });
        }
      }
    } else if (field === "contactPersonEmailId") {
      const emailValue = (vendorContactInformationForm[field] ?? "") as string;
      if (emailValue.trim() !== "" && !isValidEmail(emailValue)) {
        setVendorContactInformationFormErrors((prev) => [...prev, field]);
        showToast({
          type: "error",
          message: validationMessages?.EMAIL_VALIDATE_ERROR,
        });
      }
    }
  };
  
  const isFieldDisabled = (fieldName: string) => {
    if (isFormDisabled) return true;
    return false;
  };

  const isExistingVendorFieldsLocked = isVendorFieldsLockedForExistingVendor(
    hasExistingVendor
  );

  return (
    <Box
      className={`form-container ${isExpanded ? "form-container-expanded" : ""
        }`}
    >
      <Box
        className={`form-header cursor-pointer ${isExpanded ? "form-header-expanded" : ""
          }`}
          onClick={onToggle}
      >
        <FormHeader
          title={formHeader}
          isExpanded={isExpanded}
        />
        <Box className="pr-4 cursor-pointer">
          {isExpanded ? UpArrow("dark") : DownArrow(displayMode())}
        </Box>
      </Box>
      <form onSubmit={(e) => e.preventDefault()}>
        {isExpanded && (
          <Box className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-[1.5rem] p-[1.5rem]">
            {/* Vendor Name */}
            <Box>
              <Label
                className={`${darkMode ? "dark" : "light"} ${vendorContactInformationFormErrors.includes("vendorName")
                    ? "label-error-text"
                    : ""
                  } `}
                text={vendorContactInformationLabels?.VENDOR_NAME}
                htmlFor="vendorName"
                required={
                  vendorContactInformationFormPermissions.vendorName
                    .isMandatory
                }
              />
              <Input
                id="vendorName"
                name="vendorName"
                className={`${darkMode ? "dark" : "light"} ${vendorContactInformationFormErrors.includes("vendorName")
                    ? "field-error-color"
                    : ""
                  } `}
                value={vendorContactInformationForm.vendorName}
                onChange={handleInputChange("vendorName")}
                disabled={
                  !vendorContactInformationFormPermissions.vendorName
                    ?.isEditable ||
                  isFieldDisabled("vendorName") ||
                  isExistingVendorFieldsLocked
                }
                onFocus={() => handleFieldFocus("vendorName")}
                onBlur={() => handleBlur("vendorName")}
                maxLength={Constants.VENDOR_CORPORATE_OR_VENDOR_NAME_MAX_LENGTH}
              />
            </Box>

 {/* Corporate Country*/}
 <Box>
              <Label
                className={`${darkMode ? "dark" : "light"} ${vendorContactInformationFormErrors.includes(
                  "corporateCountry"
                )
                    ? "label-error-text"
                    : ""
                  } `}
                text={vendorContactInformationLabels?.CORPORATE_COUNTRY}
                htmlFor="corporateCountry"
                required={
                  vendorContactInformationFormPermissions.corporateCountry
                    .isMandatory
                }
              />
              {dropdownData.countries.length > 0 ? (
                <DropdownAutoSuggest
                  className={`${darkMode ? "dark" : "light"} ${
                    vendorContactInformationFormErrors.includes("corporateCountry") ? "field-error-color" : ""
                  } `}
                  options={formatRawDropdownOptions(
                    dropdownData.countries,
                    "name",
                    "id"
                  )}
                  value={(() => {
                    const countryId = vendorContactInformationForm?.corporateCountry;
                    if (!countryId) return null;
                    const country = dropdownData.countries.find(
                      (c: any) => toNumberIfPossible(c.id) === toNumberIfPossible(countryId)
                    );
                    return country ? { label: country.name, value: toNumberIfPossible(country.id) } : null;
                  })()}
                  placeholder={languageText?.PLACEHOLDERS?.COMMON?.DROP_DOWN}
                  onChange={(option) => {
                    handleDropdownChange("corporateCountry")(
                      {} as React.ChangeEvent<HTMLInputElement>,
                      option
                    );
                  }}
                  onFocus={() => handleFieldFocus("corporateCountry")}
                  onBlur={() => handleBlur("corporateCountry")}
                  isDisabled={isFieldDisabled("corporateCountry") || isExistingVendorFieldsLocked}
                  isClearable
                />
              ) : (
                <div className="text-gray-500 text-sm">Loading countries...</div>
              )}
            </Box>
            {/* Corporate Address */}
            <Box>
              <Label
                className={`${darkMode ? "dark" : "light"} ${vendorContactInformationFormErrors.includes(
                  "corporateAddress"
                )
                    ? "label-error-text"
                    : ""
                  } `}
                text={vendorContactInformationLabels?.CORPORATE_ADDRESS}
                htmlFor="vendor-contact-address-input"
                required={
                  vendorContactInformationFormPermissions.corporateAddress
                    .isMandatory
                }
              />
              {/* <Input
                className={`${darkMode ? "dark" : "light"} ${
                  vendorContactInformationFormErrors.includes(
                    "corporateAddress"
                  )
                    ? "field-error-color"
                    : ""
                } `}
                value={vendorContactInformationForm.corporateAddress}
                onChange={handleInputChange("corporateAddress")}
                disabled={
                  !vendorContactInformationFormPermissions.corporateAddress
                    ?.isEditable
                }
                onFocus={() => handleFieldFocus("corporateAddress")}
                onBlur={() => handleBlur("corporateAddress")}
                maxLength={VA_INPUT_MAX_LENGTH}
              /> */}
              <AutoComplete
                suggestions={addressSuggestions}
                inputValue={vendorContactInformationForm.corporateAddress || ""}
                setInputValue={handleAutoCompleteInputChange("corporateAddress")}
                onSuggestionsFetchRequested={() => {
                  // Get the country name from the dropdown data instead of the ID
                  const countryId = vendorContactInformationForm?.corporateCountry;
                  const countryName = dropdownData.countries.find((c: any) => 
                    (isNaN(Number(c.id)) ? c.id : Number(c.id)) === (isNaN(Number(countryId)) ? countryId : Number(countryId))
                  )?.name;
                  
                  // Map country name for Melissa API compatibility
                  const melissaCountryName = countryName ? getMelissaCountryName(countryName) : undefined;
                  handleSuggestionsFetchRequested(payloadRef, setAddressSuggestions, melissaCountryName);
                }}
                onSuggestionsClearRequested={onSuggestionsClearRequested}
                getSuggestionValue={(suggestion: any) => suggestion?.Address?.Address1}
                renderSuggestion={(suggestion: any) => <span>{suggestion?.Address?.Address}</span>}
                onSuggestionSelected={onSuggestionSelected}
                disabled={
                  !vendorContactInformationFormPermissions.corporateAddress
                    ?.isEditable || isFieldDisabled("corporateAddress") || isExistingVendorFieldsLocked
                }
                onFocus={() => handleFieldFocus("corporateAddress")}
                onBlur={() => handleBlur("corporateAddress")}
                inputProps={{
                  id: "vendor-contact-address-input",
                  name: "corporateAddress",
                  className: `${darkMode ? "dark" : "light"} ${vendorContactInformationFormErrors.includes(
                    "corporateAddress"
                  )
                      ? "field-error-color"
                      : ""
                    } `
              }}
              maxLength={Constants.ADDRESS_MAX_LENGTH}
              />
            </Box>

            {/* Corporate City*/}
            <Box>
              <Label
                className={`${darkMode ? "dark" : "light"} ${vendorContactInformationFormErrors.includes(
                  "corporateCity"
                )
                    ? "label-error-text"
                    : ""
                  } `}
                text={vendorContactInformationLabels?.CORPORATE_CITY}
                htmlFor="corporateCity"
                required={
                  vendorContactInformationFormPermissions.corporateCity
                    .isMandatory
                }
              />
              <Input
                id="corporateCity"
                name="corporateCity"
                className={`${darkMode ? "dark" : "light"} ${vendorContactInformationFormErrors.includes(
                  "corporateCity"
                )
                    ? "field-error-color"
                    : ""
                  } `}
                value={vendorContactInformationForm.corporateCity}
                onChange={handleInputChange("corporateCity")}
                onInput={handleInput("corporateCity")}
                disabled={
                  !vendorContactInformationFormPermissions.corporateCity
                    ?.isEditable || isFieldDisabled("corporateCity") || isExistingVendorFieldsLocked
                }
                onFocus={() => handleFieldFocus("corporateCity")}
                onBlur={() => handleBlur("corporateCity")}
                maxLength={Constants.ADDRESS_MAX_LENGTH}
              />
            </Box>
            {/* Corporate State*/}
            <Box>
              <Label
                className={`${darkMode ? "dark" : "light"} ${vendorContactInformationFormErrors.includes(
                  "corporateState"
                )
                    ? "label-error-text"
                    : ""
                  } `}
                text={vendorContactInformationLabels?.CORPORATE_STATE}
                htmlFor="corporateState"
                required={
                  vendorContactInformationFormPermissions.corporateState
                    .isMandatory
                }
              />
              <Input
                id="corporateState"
                name="corporateState"
                className={`${darkMode ? "dark" : "light"} ${vendorContactInformationFormErrors.includes(
                  "corporateState"
                )
                    ? "field-error-color"
                    : ""
                  } `}
                value={vendorContactInformationForm.corporateState}
                onChange={handleInputChange("corporateState")}
                onInput={handleInput("corporateState")}
                disabled={
                  !vendorContactInformationFormPermissions.corporateState
                    ?.isEditable || isFieldDisabled("corporateCity") || isExistingVendorFieldsLocked
                }
                onFocus={() => handleFieldFocus("corporateState")}
                onBlur={() => handleBlur("corporateState")}
                maxLength={Constants.ADDRESS_STATE_MAX_LENGTH}
              />
            </Box>
           

            {/* Corporate zip */}
            <Box>
              <Label
                className={`${darkMode ? "dark" : "light"} ${vendorContactInformationFormErrors.includes(
                  "corporateZip"
                )
                    ? "label-error-text"
                    : ""
                  } `}
                text={vendorContactInformationLabels?.CORPORATE_ZIP}
                htmlFor="corporateZip"
                required={
                  vendorContactInformationFormPermissions.corporateZip.isMandatory
                }
              />
              <Input
                id="corporateZip"
                name="corporateZip"
                className={`${darkMode ? "dark" : "light"} ${vendorContactInformationFormErrors.includes("corporateZip")
                    ? "field-error-color"
                    : ""
                  } `}
                value={vendorContactInformationForm.corporateZip}
                onChange={handleInputChange("corporateZip")}
                onInput={handleInput("corporateZip")}
                disabled={
                  !vendorContactInformationFormPermissions.corporateZip?.isEditable || isFieldDisabled("corporateZip") || isExistingVendorFieldsLocked
                }
                onFocus={() => handleFieldFocus("corporateZip")}
                onBlur={() => handleBlur("corporateZip")}
                minLength={Constants.ZIP_CODE_MIN_LENGTH}
                maxLength={Constants.ZIP_CODE_MAX_LENGTH}
              />
            </Box>

            {/* Contact for (sales, pricing, new items) */}
            <Box>
              <Label
                className={`${darkMode ? "dark" : "light"} ${vendorContactInformationFormErrors.includes("contactFor")
                    ? "label-error-text"
                    : ""
                  } `}
                text={vendorContactInformationLabels?.CONTACT_FOR}
                htmlFor="contactFor"
                required={
                  vendorContactInformationFormPermissions.contactFor
                    .isMandatory
                }
              >
                <span className="label-subtext">
                  (sales, pricing, new items)
                </span>
              </Label>
              <Input
                id="contactFor"
                name="contactFor"
                className={`${darkMode ? "dark" : "light"} ${vendorContactInformationFormErrors.includes("contactFor")
                    ? "field-error-color"
                    : ""
                  } `}
                value={vendorContactInformationForm.contactFor}
                onChange={handleInputChange("contactFor")}
                onInput={handleInput("contactFor")}
                disabled={
                  !vendorContactInformationFormPermissions.contactFor
                    ?.isEditable || isFieldDisabled("contactFor")
                }
                onFocus={() => handleFieldFocus("contactFor")}
                onBlur={() => handleBlur("contactFor")}
                maxLength={Constants.VA_INPUT_MAX_LENGTH}
              />
            </Box>

            {/* Contact person’s email ID */}
            <Box>
              <Label
                className={`${darkMode ? "dark" : "light"} ${vendorContactInformationFormErrors.includes(
                  "contactPersonEmailId"
                )
                    ? "label-error-text"
                    : ""
                  } `}
                text={vendorContactInformationLabels?.CONTACT_PERSON_EMAIL_ID}
                htmlFor="contactPersonEmailId"
                required={
                  vendorContactInformationFormPermissions
                    .contactPersonEmailId.isMandatory
                }
              />
              <Input
                id="contactPersonEmailId"
                name="contactPersonEmailId"
                className={`${darkMode ? "dark" : "light"} ${vendorContactInformationFormErrors.includes(
                  "contactPersonEmailId"
                )
                    ? "field-error-color"
                    : ""
                  } `}
                value={vendorContactInformationForm.contactPersonEmailId}
                onChange={handleInputChange("contactPersonEmailId")}
                onInput={handleInput("contactPersonEmailId")}
                disabled={
                  !vendorContactInformationFormPermissions
                    .contactPersonEmailId?.isEditable || isFieldDisabled("contactPersonEmailId")
                }
                onFocus={() => handleFieldFocus("contactPersonEmailId")}
                onBlur={() => handleBlur("contactPersonEmailId")}
                maxLength={Constants.VA_INPUT_MAX_LENGTH}
              />
            </Box>

            {/* Contact person’s Phone */}
            <Box>
              <Label
                className={`${darkMode ? "dark" : "light"} ${vendorContactInformationFormErrors.includes(
                  "contactPersonPhone"
                )
                    ? "label-error-text"
                    : ""
                  } `}
                text={vendorContactInformationLabels?.CONTACT_PERSON_PHONE}
                htmlFor="contactPersonPhone"
                required={
                  vendorContactInformationFormPermissions
                    .contactPersonPhone.isMandatory
                }
              />
              <Input
                id="contactPersonPhone"
                name="contactPersonPhone"
                className={`${darkMode ? "dark" : "light"} ${vendorContactInformationFormErrors.includes(
                  "contactPersonPhone"
                )
                    ? "field-error-color"
                    : ""
                  } `}
                value={vendorContactInformationForm.contactPersonPhone}
                onChange={handleInputChange("contactPersonPhone")}
                onInput={handleInput("contactPersonPhone")}
                disabled={
                  !vendorContactInformationFormPermissions
                    .contactPersonPhone?.isEditable || isFieldDisabled("contactPersonPhone")
                }
                onFocus={() => handleFieldFocus("contactPersonPhone")}
                onBlur={() => handleBlur("contactPersonPhone")}
                maxLength={Constants.VA_INPUT_MAX_LENGTH}
              />
            </Box>
            <ConfirmationModal
              open={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              onConfirm={()=>setIsModalOpen(false)}
              onDecline={handleSelectAlternative}
              confirmText="Confirm Entered"
              cancelText="Select Alternative"
              showCancelButton={true}
            />
          </Box>
        )}
      </form>
    </Box>
  );
};

export default VendorContactInformationForm;